import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { strings } from 'src/localization';
import { ComponentMapIds } from 'src/components/map';
import { IView } from 'Framework';
import css from './ResetPassword.module.scss';
import SVPButton from '../SVPButtons/SVPButton';
import { Customer, VehicleRec, ascCtaInteractionInput, ascFormEngagementInput, ascPageviewInput, ascFormSubmissionInput } from 'src/models';
import { PasswordField, TextField, LoadingProgress } from '@framework-controls/core';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { ascCtaInteraction, ascFormEngagement, ascPageview, getElementColor, ascFormSubmission } from 'src/helpers/EventHelper/EventHelper';

export interface IProps {
    rtsToken: string;
    custInfo?: Customer;
    pushToMainPanes: (inputView: IView) => void;
    resetCustPassword: (resetObject: { inputAcctId: string, custEmail: string, newPassword: string },
        profileNum: string, store: string, branch: string) => void;
    ClearTokenData: () => void;
    specProfile: string;
    store: string,
    branch: string,
    errMsg?: string;
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    clearError: () => void;
    mainPaneViewOrder: IView[];
    getVehicles: () => void;
    theme?: string;
    locale: string;
    identityVerified: boolean;
}
// define password typing
type PasswordError = 'confirm' | 'length' | '';

const minimumLength = 6;
const maximumLength = 10;

const ResetPassword = (props: IProps) => {
    const [password, setPassword] = useState('');
    const [conPass, setConPass] = useState('');
    const [tokenClear, setTokenClear] = useState(true);
    const [error, setError] = useState<PasswordError>('');
    const [resetPassword, setResetPassword] = useState(false);
    const [awaitResponse, setAwaitResponse] = useState(false);

    // This logic is just to force a rebuild if locale changes.  If should not really do anything.
    useEffect(() => {
        if (props.locale === 'xx') {
            // setForgotCSS(css.passLitAccess);

        }
    }, [props.locale]);

    const loggedEmail = props.custInfo!.emailAddress;
    const acctId: string = props.custInfo!.nameId;
    if (tokenClear) {
        props.ClearTokenData();
        setTokenClear(false);
    }

    const passwordHandler = useCallback((_, value: string) => {
        setPassword(value);
        setError(confirmMatch(value, conPass));
    }, [conPass]);

    const confirmPasswordHandler = useCallback((_, value: string) => {
        setConPass(value);
        setError(confirmMatch(password, value));
    }, [password]);

    const resetBtnHandler = useCallback(() => {
        const reqResetObj = {
            inputAcctId: acctId,
            custEmail: loggedEmail,
            newPassword: password,
        };
        setResetPassword(true);
        props.resetCustPassword(reqResetObj, props.specProfile, props.store, props.branch);
        const ascFormSubmissionInp: ascFormSubmissionInput = {
            pageType: 'service',
            commType: 'form',
            commOutcome: 'crm_update',
            submissionId: props.custInfo?.ascCustId! ?? '',
            elementText: strings.Reset,
            department: 'service',
            formName: 'ResetPassword',
            formType: 'consumer_contact',
        };
        ascFormSubmission(ascFormSubmissionInp);
        setAwaitResponse(true);
    }, [
        acctId,
        loggedEmail,
        password,
        props?.resetCustPassword,
        props?.specProfile,
        props.store,
        props.branch,
        props.custInfo?.ascCustId,
    ]);

    const tryAgainBtnHandler = useCallback((e: any) => {
        props.clearError();
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.TryAgain,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, props.vehicles, props.selectedVehicle]);

    const closeHandler = useCallback((e: any) => {
        props.clearError();
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props.clearError, props.vehicles, props.selectedVehicle]);

    const blurHandler = useCallback((id: string) => {
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: id === 'Password' ? strings.Password : strings.ConfirmPassword,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp);
    }, []);

    const errorMessage = useMemo(() => {
        const viewPaneLength = props.mainPaneViewOrder.length;
        const currentViewPane = props.mainPaneViewOrder[viewPaneLength - 1];
        if (currentViewPane.viewId !== ComponentMapIds.ResetPassword) {
            return null;
        }
        if (props.errMsg && props.errMsg !== '0000' && props.custInfo) {
            setAwaitResponse(false);

            return GetErrorMessage(tryAgainBtnHandler, closeHandler);
        }
        setResetPassword(false);

        return null;
    }, [props.errMsg, tryAgainBtnHandler, closeHandler,
        props.custInfo, props.mainPaneViewOrder]);

    useEffect(() => {
        if (!resetPassword && awaitResponse) {
            props.pushToMainPanes({
                viewId: ComponentMapIds.UserAccountMenu,
                component: ComponentMapIds.UserAccountMenu,
            });
            setAwaitResponse(false);
        }
    }, [props.pushToMainPanes, resetPassword, awaitResponse, errorMessage]);

    useEffect(() => {
        const viewPaneLength = props.mainPaneViewOrder.length;
        const currentViewPane = props.mainPaneViewOrder[viewPaneLength - 1];

        if (props.custInfo && currentViewPane.viewId === ComponentMapIds.ResetPassword) {
            props.getVehicles();
        }
    }, [
        props.custInfo,
        props.mainPaneViewOrder,
        props.getVehicles,
    ]);

    useEffect(() => {
        if (props.mainPaneViewOrder){
            if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId === ComponentMapIds.ResetPassword) {
                const ascPageViewInp: ascPageviewInput = {
                    pageType: 'service',
                    errorCode: '',
                    itemResults: '',
                };
                ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
            }
        }
    }, [props.mainPaneViewOrder, props.vehicles, props.selectedVehicle]);

    return (
        <div className={css.ResetPassword}>
            <div className={css.topContainer}>
                <h2>{strings.ResetPass}</h2>
                <h3>{strings.Hello} {props.identityVerified ? props.custInfo!.firstName : ''}{strings.EnterNewPassword}</h3>
                {/* <p>
                    <span>{strings.Hello} {props.custInfo!['Customer']?.firstName}{strings.EnterNewPassword}</span>
                </p> */}
                <hr />
            </div>
            {resetPassword ? <LoadingProgress cssClasses={{ spinner: css.progressColor }} size={100} /> : null}
            {errorMessage}
            <div className={css.PasswordBox}>
                <span>{strings.RequiredFields}</span>
                <div className={css.Input2}>
                    <PasswordField
                        id='Password'
                        label={strings.Password}
                        value={password}
                        size='full'
                        onChange={passwordHandler}
                        onBlur={blurHandler}
                        minLength={minimumLength}
                        maxLength={maximumLength}
                        useAutoComplete={false}
                    />
                </div>
                <div className={css.Input2}>
                    <TextField
                        id='ConfirmPassword'
                        label={strings.ConfirmPassword}
                        value={conPass}
                        size='full'
                        onChange={confirmPasswordHandler}
                        onBlur={blurHandler}
                        type='password'
                        error={error === 'confirm'}
                        helperText={error === 'confirm' ? strings.PasswordMismatch : ''}
                        useAutocomplete={false}
                    />
                </div>
                <div className={css.ResetBtn}>
                    <SVPButton
                        id='ResetBtn'
                        message={strings.Reset}
                        variant={'primaryBtnAlt2'}
                        handler={resetBtnHandler}
                        noArrow
                        isDisabled={!!error || !conPass || !password}
                        theme={props.theme}
                    />
                </div>
            </div>
        </div>
    );
};

function GetErrorMessage(
    tryAgainHandler: (e: any) => void,
    closeHandler: (e: any) => void) {
    const headerMsg = strings.IdenticalPassword;
    const errorMsg = strings.NewPassword;
    const primaryMessage = strings.TryAgain;
    const primaryHandler = tryAgainHandler;

    return (
        <ErrorMessage
            headerMsg={headerMsg}
            errorMsg={errorMsg}
            primaryBtnMessage={primaryMessage}
            secondaryBtnMessage={''}
            primaryHandler={primaryHandler}
            secondaryHandler={closeHandler}
            closeHandler={closeHandler}
        />
    );
}

const confirmMatch = (pass: string, cPass: string) => {
    if (pass.length > maximumLength || pass.length < minimumLength) {
        return 'length';
    } else if (cPass && pass !== cPass) {
        return 'confirm';
    }

    return '';
};

export default ResetPassword;